import { useEffect, useState } from "react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { buildHead } from "@/lib/seo";

const GOLD = "#C8A84B";

type LoginSearch = { redirect?: string };

function safeRedirect(target: string | undefined): string {
  if (!target) return "/portal";
  if (!target.startsWith("/") || target.startsWith("//")) return "/portal";
  return target;
}

export const Route = createFileRoute("/login")({
  validateSearch: (s: Record<string, unknown>): LoginSearch => ({
    redirect: typeof s.redirect === "string" ? s.redirect : undefined,
  }),
  head: () =>
    buildHead({
      locale: "en",
      title: "Sign in · ShugertGEO portal",
      description: "Access your GEO Readiness Audit portal via magic link.",
      path: "/login",
      altPath: "/login",
      noindex: true,
    }),
  component: LoginPage,
});

function LoginPage() {
  const router = useRouter();
  const { redirect } = Route.useSearch();
  const redirectTo = safeRedirect(redirect);
  const [emailInput, setEmailInput] = useState("");
  const [state, setState] = useState<
    | { kind: "idle" }
    | { kind: "sending" }
    | { kind: "sent" }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active && data.session?.user) {
        router.navigate({ to: redirectTo });
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session?.user) router.navigate({ to: redirectTo });
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, [router, redirectTo]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setState({ kind: "sending" });
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: emailInput.trim(),
        options: {
          shouldCreateUser: true,
          emailRedirectTo: `${window.location.origin}${redirectTo}`,
        },
      });
      if (error) throw error;
      setState({ kind: "sent" });
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "Couldn't send link",
      });
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card/40 p-8">
        <p
          className="text-[10px] font-medium uppercase tracking-[0.22em]"
          style={{ color: GOLD }}
        >
          Client portal
        </p>
        <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground">
          Access Your Audit Portal
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Enter the email you used at checkout and we'll send you a one-tap
          sign-in link.
        </p>

        {state.kind === "sent" ? (
          <div className="mt-8 rounded-xl border border-border bg-background/50 p-5 text-sm text-foreground">
            Check your email for a sign-in link.
            <p className="mt-2 text-xs text-muted-foreground">
              Sent to <span className="font-medium">{emailInput}</span>. Link
              expires in 60 minutes.
            </p>
            <button
              type="button"
              onClick={() => setState({ kind: "idle" })}
              className="mt-4 text-xs text-muted-foreground underline hover:text-foreground"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label
                htmlFor="login-email"
                className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground"
              >
                Email
              </label>
              <input
                id="login-email"
                type="email"
                required
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="mt-2 w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground focus:border-foreground focus:outline-none"
              />
            </div>
            {state.kind === "error" && (
              <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {state.message}
              </p>
            )}
            <button
              type="submit"
              disabled={state.kind === "sending"}
              className="w-full rounded-full px-6 py-3 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: GOLD }}
            >
              {state.kind === "sending" ? "Sending…" : "Send Magic Link"}
            </button>
          </form>
        )}

        <p className="mt-8 text-xs text-muted-foreground">
          Need help?{" "}
          <a href="mailto:hello@shugert.com.mx" className="underline">
            hello@shugert.com.mx
          </a>
          {" · "}
          <Link to="/" className="underline">
            Back to site
          </Link>
        </p>
      </div>
    </div>
  );
}